/**
 * iter150AZ.22 — Warranty claim picker for the "New Warranty quote"
 * flow.
 *
 * The user picks Warranty from the Create-Quote dropdown → this modal
 * opens, lists open warranty claims for the customer, and lets the
 * user pick one. The backend then ties the new quote to that claim.
 *
 * Strict: a quote can only be created against an existing claim
 * (matches the user's design choice — b.2 in iter150AZ.22 plan).
 */
import { useEffect, useState } from "react";
import { X, Spinner, ShieldCheck, ArrowSquareOut } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

export default function WarrantyClaimPickerModal({
  customerId, customerName, isOpen, onClose, onPick,
}) {
  const t = useT();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState("");

  useEffect(() => {
    if (!isOpen || !customerId) return;
    setLoading(true);
    setError("");
    setSelected("");
    api.get("/admin/warranty/claims",
            { params: { customer_id: customerId, limit: 50 } })
      .then(r => {
        const all = r.data?.items || r.data?.claims || [];
        // Only "open"-ish claims are quote-able (i.e. not closed/rejected).
        const open = all.filter(c =>
          !["closed", "rejected", "expired"].includes((c.status || "").toLowerCase()));
        setClaims(open);
      })
      .catch(e => setError(e?.response?.data?.detail || e?.message || "Could not load claims"))
      .finally(() => setLoading(false));
  }, [isOpen, customerId]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      data-testid="warranty-claim-picker-modal"
    >
      <div className="bg-white border border-slate-200 rounded-sm shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-amber-50">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} weight="bold" className="text-amber-700" />
            <h3 className="text-sm font-bold font-['Chivo'] text-slate-900">
              {t("warranty_picker.title", "Link warranty claim")}
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="warranty-claim-picker-close"
            className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-amber-100 text-slate-500"
          >
            <X size={14} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-xs text-slate-600 mb-3">
            {t("warranty_picker.subtitle",
              "A Warranty quote must be linked to an existing claim. Pick the claim this quote covers, or open the warranty page to file a new one first.")}
          </p>
          <div className="text-[11px] text-slate-500 mb-2">
            <span className="font-semibold">{customerName}</span>{" "}
            <span className="font-mono">({customerId})</span>
          </div>

          {loading && (
            <div className="py-8 text-center text-slate-400">
              <Spinner size={20} className="animate-spin inline" />
            </div>
          )}
          {!loading && error && (
            <div data-testid="warranty-claim-picker-error"
                 className="bg-red-50 border border-red-200 rounded-sm p-3 text-xs text-red-700">
              {error}
            </div>
          )}
          {!loading && !error && claims.length === 0 && (
            <div data-testid="warranty-claim-picker-empty"
                 className="text-center py-6 space-y-2">
              <p className="text-sm text-slate-500">
                {t("warranty_picker.no_open_claims",
                  "No open warranty claims for this customer.")}
              </p>
              <a
                href={`/warranty/claim?customer_id=${encodeURIComponent(customerId)}`}
                target="_blank" rel="noopener noreferrer"
                data-testid="warranty-claim-picker-open-claim-page"
                className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline"
              >
                <ArrowSquareOut size={11} weight="bold" />
                {t("warranty_picker.file_new", "File a warranty claim first")}
              </a>
            </div>
          )}
          {!loading && !error && claims.length > 0 && (
            <ul className="space-y-1.5" data-testid="warranty-claim-picker-list">
              {claims.map(claim => {
                const isSel = selected === claim.id;
                return (
                  <li key={claim.id}>
                    <button
                      onClick={() => setSelected(claim.id)}
                      data-testid={`warranty-claim-pick-${claim.id}`}
                      className={`w-full text-left rounded-sm p-2.5 border transition-colors ${
                        isSel
                          ? "border-amber-500 bg-amber-50"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-mono text-slate-700">
                          {claim.id}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200">
                          {claim.status || "open"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-800 mt-0.5 truncate">
                        {claim.title || claim.subject || claim.compressor_serial || "(no subject)"}
                      </div>
                      {claim.compressor_serial && claim.title && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          SN: {claim.compressor_serial}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            data-testid="warranty-claim-picker-cancel"
            className="h-8 px-3 text-xs font-semibold rounded-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-700"
          >
            {t("action.cancel", "Cancel")}
          </button>
          <button
            onClick={() => { if (selected) onPick(selected); }}
            disabled={!selected}
            data-testid="warranty-claim-picker-confirm"
            className="h-8 px-3 text-xs font-bold rounded-sm bg-amber-700 hover:bg-amber-800 text-white disabled:opacity-40"
          >
            {t("warranty_picker.create_quote", "Create warranty quote")}
          </button>
        </footer>
      </div>
    </div>
  );
}
